interface Student {
  id: string;
  name: string;
}

interface AssignedStory {
  id: string;
}

interface Group {
  id: string;
  name: string;
  students: Student[];
  assignedStories: AssignedStory[];
  updatedAt: string;
  isDefault?: boolean; // Optional flag to indicate if it's the default group
}

interface GroupsResponse {
  schoolId: string;
  classId: string;
  defaultGroup: Group;
  customGroups: Group[];
}

/**
 * Helper functions for group operations
 */
export class GroupUtils {
  
  /**
   * Check if a group is the default group (cannot be edited or deleted)
   */
  static isDefaultGroup(groupId: string, data: GroupsResponse): boolean {
    return data.defaultGroup.id === groupId;
  }

  /**
   * Check if a group can be edited (only custom groups can be edited)
   */
  static canEditGroup(groupId: string, data: GroupsResponse): boolean {
    return !GroupUtils.isDefaultGroup(groupId, data);
  }

  /**
   * Check if a group can be deleted (only custom groups can be deleted)
   */
  static canDeleteGroup(groupId: string, data: GroupsResponse): boolean {
    return !GroupUtils.isDefaultGroup(groupId, data);
  }

  /**
   * Find a group by ID from the groups data
   */
  static findGroupById(groupId: string, data: GroupsResponse): Group | null {
    if (data.defaultGroup.id === groupId) {
      return data.defaultGroup;
    }
    
    return data.customGroups.find(group => group.id === groupId) || null;
  }

  /**
   * Get all groups (default + custom) as a flat array
   */
  static getAllGroups(data: GroupsResponse): Group[] {
    return [data.defaultGroup, ...data.customGroups];
  }

  /**
   * Get only custom groups (editable/deletable groups)
   */
  static getCustomGroups(data: GroupsResponse): Group[] {
    return data.customGroups;
  }

  /**
   * Get students from a specific group
   */
  static getGroupStudents(groupId: string, data: GroupsResponse): Student[] {
    const group = GroupUtils.findGroupById(groupId, data);
    return group ? group.students : [];
  }

  /**
   * Get assigned stories from a specific group
   */
  static getGroupAssignedStories(groupId: string, data: GroupsResponse): AssignedStory[] {
    const group = GroupUtils.findGroupById(groupId, data);
    return group ? group.assignedStories : [];
  }

  /**
   * Get all students across all groups (deduplicated)
   */
  static getAllStudents(data: GroupsResponse): Student[] {
    const allStudents = new Map<string, Student>();
    
    // Add students from default group
    data.defaultGroup.students.forEach(student => {
      allStudents.set(student.id, student);
    });
    
    // Add students from custom groups
    data.customGroups.forEach(group => {
      group.students.forEach(student => {
        allStudents.set(student.id, student);
      });
    });
    
    return Array.from(allStudents.values());
  }

  /**
   * Find which groups a student belongs to
   */
  static findStudentGroups(studentId: string, data: GroupsResponse): Group[] {
    const groups: Group[] = [];
    
    // Check default group
    if (data.defaultGroup.students.some(student => student.id === studentId)) {
      groups.push(data.defaultGroup);
    }
    
    // Check custom groups
    data.customGroups.forEach(group => {
      if (group.students.some(student => student.id === studentId)) {
        groups.push(group);
      }
    });
    
    return groups;
  }

  /**
   * Get group statistics
   */
  static getGroupStats(data: GroupsResponse) {
    return {
      totalGroups: 1 + data.customGroups.length,
      customGroupsCount: data.customGroups.length,
      totalStudents: GroupUtils.getAllStudents(data).length,
      defaultGroupStudents: data.defaultGroup.students.length,
      averageStudentsPerCustomGroup: data.customGroups.length > 0 
        ? data.customGroups.reduce((sum, group) => sum + group.students.length, 0) / data.customGroups.length 
        : 0
    };
  }

  /**
   * Get students who are in other custom groups (excluding the current group being edited)
   */
  static getStudentsInOtherCustomGroups(groups: Group[], currentGroupId?: string): string[] {
    const studentsInOtherGroups: string[] = [];
    console.log('currentGroupId', currentGroupId, groups);
    
    
    groups.forEach(group => {
      // Skip default groups and the current group being edited
      if (!group.isDefault && group.id !== currentGroupId) {
        group.students.forEach(student => {
          if (!studentsInOtherGroups.includes(student.name)) {
            studentsInOtherGroups.push(student.name);
          }
        });
      }
    });

    console.log(studentsInOtherGroups, 'studentsInOtherGroups');
    
    
    return studentsInOtherGroups;
  }
}

export default GroupUtils;